import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import { useAuthStore } from '../../../../auth/state';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { useNavigate } from 'react-router-dom';
import './pricing.css';

// # Pricing Management component
const Pricing = () => {
  const navigate = useNavigate();
  const [showEgyptianPrices, setShowEgyptianPrices] = useState(true);
  const { user, isAdmin } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [error, setError] = useState(null);

  // # Hero section background style
  const heroStyle = {
    background: `linear-gradient(rgba(44, 62, 80, 0.7), rgba(44, 62, 80, 0.7)), url(${process.env.PUBLIC_URL + '/icons/productsx.jpg'})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  // # Check admin access and redirect if not authorized
  useEffect(() => {
    if (!isLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, isLoading, navigate]);

  // # State for storing pricing data
  const [pricingData, setPricingData] = useState({
    egyptian_pricing: {
      step1_library: {
        last_edited_at: null,
        last_edited_by: null,
        "6_months": {
          pricing: "1500",
          currency: "EGP"
        },
        "12_months": {
          pricing: "2250",
          currency: "EGP"
        }
      },
      step2_library: {
        last_edited_at: null,
        last_edited_by: null,
        "6_months": {
          pricing: "1500",
          currency: "EGP"
        },
        "12_months": {
          pricing: "2250",
          currency: "EGP"
        }
      },
      step1_mentorship: {
        last_edited_at: null,
        last_edited_by: null,
        deposit: "1800",
        monthly: "800",
        currency: "EGP"
      },
      step2_mentorship: {
        last_edited_at: null,
        last_edited_by: null,
        deposit: "1800",
        monthly: "800",
        currency: "EGP"
      },
      oet_preparation: {
        last_edited_at: null,
        last_edited_by: null,
        "1_month": {
          pricing: "1500",
          currency: "EGP"
        }
      },
      visa_counselling: {
        last_edited_at: null,
        last_edited_by: null,
        "1_month": {
          pricing: "500",
          currency: "EGP"
        }
      }
    },
    international_pricing: {
      step1_library: {
        last_edited_at: null,
        last_edited_by: null,
        "6_months": {
          pricing: "60",
          currency: "USD"
        },
        "12_months": {
          pricing: "90",
          currency: "USD"
        }
      },
      step2_library: {
        last_edited_at: null,
        last_edited_by: null,
        "6_months": {
          pricing: "60",
          currency: "USD"
        },
        "12_months": {
          pricing: "90",
          currency: "USD"
        }
      },
      step1_mentorship: {
        last_edited_at: null,
        last_edited_by: null,
        deposit: "70",
        monthly: "30",
        currency: "USD"
      },
      step2_mentorship: {
        last_edited_at: null,
        last_edited_by: null,
        deposit: "70",
        monthly: "30",
        currency: "USD"
      },
      oet_preparation: {
        last_edited_at: null,
        last_edited_by: null,
        "1_month": {
          pricing: "60",
          currency: "USD"
        }
      },
      visa_counselling: {
        last_edited_at: null,
        last_edited_by: null,
        "1_month": {
          pricing: "20",
          currency: "USD"
        }
      }
    }
  });

  // # Fetch pricing data from Firestore
  useEffect(() => {
    const fetchPricingData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (!user || !isAdmin) {
          throw new Error('Unauthorized access');
        }

        const pricingDoc = await getDoc(doc(db, 'technicals', 'pricing'));
        if (pricingDoc.exists()) {
          // # Merge the fetched data with the default state to ensure all required fields exist
          const fetchedData = pricingDoc.data();
          const mergedData = {
            egyptian_pricing: {
              ...pricingData.egyptian_pricing,
              ...fetchedData.egyptian_pricing
            },
            international_pricing: {
              ...pricingData.international_pricing,
              ...fetchedData.international_pricing
            }
          };
          setPricingData(mergedData);
        } else {
          // # Initialize pricing document if it doesn't exist
          await setDoc(doc(db, 'technicals', 'pricing'), pricingData);
        }
      } catch (error) {
        console.error('Error fetching pricing data:', error);
        setError('Failed to load pricing data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPricingData();
  }, [user, isAdmin]);

  // # Handle price editing
  const handlePriceEdit = (productType, duration, price, isInternational) => {
    if (!user || !isAdmin) {
      setError('You must be an admin to edit prices');
      return;
    }
    
    setEditingPrice({
      productType,
      duration,
      price,
      isInternational
    });
    setEditMode(true);
    setError(null);
  };

  // # Save price changes to Firestore
  const handlePriceSave = async (productType, duration, newPrice, isInternational) => {
    setIsLoading(true);
    setError(null);
    try {
      if (!user || !isAdmin) {
        throw new Error('Unauthorized access');
      }

      const pricingType = isInternational ? 'international_pricing' : 'egyptian_pricing';
      const updatedData = { ...pricingData };
      const currency = isInternational ? 'USD' : 'EGP';
      
      // # Update the specific price
      if (duration === 'deposit' || duration === 'monthly') {
        updatedData[pricingType][productType][duration] = newPrice;
        updatedData[pricingType][productType].currency = currency;
      } else {
        updatedData[pricingType][productType][duration].pricing = newPrice;
        updatedData[pricingType][productType][duration].currency = currency;
      }

      // # Update metadata
      updatedData[pricingType][productType].last_edited_at = new Date().toISOString();
      updatedData[pricingType][productType].last_edited_by = user.email;

      // # Save to Firestore
      await setDoc(doc(db, 'technicals', 'pricing'), updatedData);
      
      // # Update local state
      setPricingData(updatedData);
      setEditMode(false);
      setEditingPrice(null);
    } catch (error) {
      console.error('Error saving price:', error);
      setError('Failed to save price. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // # Generate subscription data from pricing data
  const generateSubscriptions = (isInternational) => {
    const pricingType = isInternational ? 'international_pricing' : 'egyptian_pricing';
    const currency = isInternational ? '$' : 'LE';
    
    return [
      {
        title: 'USMLE Step 1 Library',
        type: 'library',
        description: 'Access to comprehensive Step 1 study materials',
        durations: [
          { 
            months: 6, 
            price: `${currency}${pricingData[pricingType].step1_library['6_months'].pricing}`,
            features: ['6 months access', 'Up to 2 months free with exam scheduling', 'Group discounts available (65% off for groups of 5+)']
          },
          { 
            months: 12, 
            price: `${currency}${pricingData[pricingType].step1_library['12_months'].pricing}`,
            features: ['12 months access', 'Up to 4 months free with exam scheduling', 'Group discounts available (65% off for groups of 5+)']
          }
        ],
        productType: 'step1_library'
      },
      {
        title: 'USMLE Step 2 Library',
        type: 'library',
        description: 'Access to comprehensive Step 2 study materials',
        durations: [
          { 
            months: 6, 
            price: `${currency}${pricingData[pricingType].step2_library['6_months'].pricing}`,
            features: ['6 months access', 'Up to 2 months free with exam scheduling', 'Group discounts available (65% off for groups of 5+)']
          },
          { 
            months: 12, 
            price: `${currency}${pricingData[pricingType].step2_library['12_months'].pricing}`,
            features: ['12 months access', 'Up to 4 months free with exam scheduling', 'Group discounts available (65% off for groups of 5+)']
          }
        ],
        productType: 'step2_library'
      },
      {
        title: 'USMLE Step 1 Mentorship',
        type: 'mentorship',
        description: 'Complete Step 1 mentorship program including library access and tutoring',
        isService: true,
        price: `${currency}${pricingData[pricingType].step1_mentorship.deposit} deposit + ${currency}${pricingData[pricingType].step1_mentorship.monthly}/month`,
        features: [
          'Full Step 1 library access',
          'Complete mentorship and tutoring',
          'Personalized study plans',
          'Regular progress tracking'
        ],
        productType: 'step1_mentorship'
      },
      {
        title: 'USMLE Step 2 Mentorship',
        type: 'mentorship',
        description: 'Complete Step 2 mentorship program including library access and tutoring',
        isService: true,
        price: `${currency}${pricingData[pricingType].step2_mentorship.deposit} deposit + ${currency}${pricingData[pricingType].step2_mentorship.monthly}/month`,
        features: [
          'Full Step 2 library access',
          'Complete mentorship and tutoring',
          'Personalized study plans',
          'Regular progress tracking'
        ],
        productType: 'step2_mentorship'
      },
      {
        title: 'OET Preparation',
        type: 'service',
        description: 'OET Preparation Tips for reading, speaking, writing, and listening',
        durations: [
          { 
            months: 1, 
            price: `${currency}${pricingData[pricingType].oet_preparation['1_month'].pricing}`,
            features: [
              'Comprehensive OET exam preparation',
              'Tips for all four components',
              'Practice materials and guidance',
              'Expert feedback and support'
            ]
          }
        ],
        productType: 'oet_preparation'
      },
      {
        title: 'US VISA Counselling',
        type: 'service',
        description: 'Quick US Visa interview tips and mock training',
        durations: [
          { 
            months: 1, 
            price: `${currency}${pricingData[pricingType].visa_counselling['1_month'].pricing}`,
            features: [
              'Interview preparation tips',
              'Mock interview session',
              'Document preparation guidance',
              'Common pitfalls to avoid'
            ]
          }
        ],
        productType: 'visa_counselling'
      }
    ];
  };

  const subscriptionTypes = showEgyptianPrices ? 
    generateSubscriptions(false) : 
    generateSubscriptions(true);

  // # Render price input for editing
  const renderPriceInput = (subscription, duration) => {
    if (!editMode || !editingPrice) return null;
    
    const isCurrentlyEditing = editingPrice.productType === subscription.productType && 
                             editingPrice.duration === duration;
    
    if (!isCurrentlyEditing) return null;

    return (
      <div className="pricing_edit-controls">
        <input
          type="number"
          value={editingPrice.price}
          onChange={(e) => setEditingPrice({
            ...editingPrice,
            price: e.target.value
          })}
          className="pricing_price-input"
        />
        <div className="pricing_edit-buttons">
          <button
            onClick={() => handlePriceSave(
              subscription.productType,
              duration,
              editingPrice.price,
              !showEgyptianPrices
            )}
            className="pricing_save-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => {
              setEditMode(false);
              setEditingPrice(null);
            }}
            className="pricing_cancel-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // # Handle subscription actions
  const handleSubscriptionAction = (subscription) => {
    navigate('/products');
  };

  return (
    <div className="pricing_container">
      <SharedHeader />
      
      {error && (
        <div className="pricing_error-message">
          {error}
        </div>
      )}

      <main className="pricing_content">
        <section className="pricing_hero-section" style={heroStyle}>
          <div className="pricing_hero-overlay"></div>
          <h1>Pricing Management</h1>
          <p>Manage and update subscription prices for all USMLE preparation resources</p>
          <div className="pricing_hero-decoration">
            <div className="pricing_hero-shape"></div>
          </div>
        </section>

        {isLoading ? (
          <div className="pricing_loading">
            Loading...
          </div>
        ) : (
          <>
            <div className="pricing_toggle">
              <button 
                className={`pricing_toggle-btn ${!showEgyptianPrices ? 'pricing_active' : ''}`}
                onClick={() => setShowEgyptianPrices(false)}
              >
                International Pricing
              </button>
              <button 
                className={`pricing_toggle-btn ${showEgyptianPrices ? 'pricing_active' : ''}`}
                onClick={() => setShowEgyptianPrices(true)}
              >
                Egyptian Pricing
              </button>
            </div>

            {showEgyptianPrices && (
              <div className="pricing_pricing-notice">
                <img 
                  src={process.env.PUBLIC_URL + '/icons/landing/Egypt.png'} 
                  alt="Egyptian Flag" 
                  className="pricing_pricing-notice-icon"
                />
                <p>
                  For Egyptian medical students and graduates, we provide a <strong>50%</strong> discount 
                  on single applications, and a <strong>65%</strong> discount on group applications 
                  (minimum group of <strong>five</strong> students).
                  <br />
                  <br />
                  <span className="pricing_pricing-notice-disclaimer">
                    * Prices are approximate <strong>according to USD exchange rate</strong> and may change periodically.
                  </span>
                </p>
              </div>
            )}

            <section className="pricing_subscription-types">
              <div className="pricing_subscription-container">
                {/* Library Subscriptions Row */}
                <div className="pricing_library-row">
                  {subscriptionTypes
                    .filter(sub => sub.type === 'library')
                    .map((sub, index) => (
                      <div key={index} className="pricing_subscription-card">
                        <h2>{sub.title}</h2>
                        <p className="pricing_sub-description">{sub.description}</p>
                        
                        <div className="pricing_duration-options">
                          {sub.durations.map((duration, idx) => (
                            <div key={idx} className="pricing_duration-item">
                              <div className="pricing_duration-header">
                                <span className="pricing_duration">{duration.months} Months</span>
                                <div className="pricing_price-container">
                                  <span className="pricing_price">{duration.price}</span>
                                  <button
                                    className="pricing_edit-btn"
                                    onClick={() => handlePriceEdit(
                                      sub.productType,
                                      duration.months === 6 ? '6_months' : '12_months',
                                      duration.price.replace(/[^0-9]/g, ''),
                                      !showEgyptianPrices
                                    )}
                                  >
                                    Edit
                                  </button>
                                </div>
                              </div>
                              {renderPriceInput(sub, duration.months === 6 ? '6_months' : '12_months')}
                              <ul className="pricing_features-list">
                                {duration.features.map((feature, fidx) => (
                                  <li key={fidx}>{feature}</li>
                                ))}
                              </ul>
                              <div className="pricing_subscription-buttons">
                                <button 
                                  className="pricing_subscribe-btn"
                                  onClick={() => handleSubscriptionAction(sub)}
                                >
                                  To Products
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>

                {/* Mentorship Subscriptions Row */}
                <div className="pricing_mentorship-row">
                  {subscriptionTypes
                    .filter(sub => sub.type === 'mentorship')
                    .map((sub, index) => (
                      <div key={index} className="pricing_subscription-card">
                        <h2>{sub.title}</h2>
                        <p className="pricing_sub-description">{sub.description}</p>
                        
                        <div className="pricing_service-pricing">
                          <div className="pricing_price-header">
                            <div className="pricing_price-container">
                              <span className="pricing_price">{sub.price}</span>
                              <div className="pricing_edit-mentorship">
                                <button
                                  className="pricing_edit-btn"
                                  onClick={() => handlePriceEdit(
                                    sub.productType,
                                    'deposit',
                                    sub.price.split(' ')[0].replace(/[^0-9]/g, ''),
                                    !showEgyptianPrices
                                  )}
                                >
                                  Edit Deposit
                                </button>
                                <button
                                  className="pricing_edit-btn"
                                  onClick={() => handlePriceEdit(
                                    sub.productType,
                                    'monthly',
                                    sub.price.split(' ')[3].replace(/[^0-9]/g, ''),
                                    !showEgyptianPrices
                                  )}
                                >
                                  Edit Monthly
                                </button>
                              </div>
                            </div>
                          </div>
                          {renderPriceInput(sub, editingPrice?.duration || '')}
                          <ul className="pricing_features-list">
                            {sub.features.map((feature, fidx) => (
                              <li key={fidx}>{feature}</li>
                            ))}
                          </ul>
                          <div className="pricing_subscription-buttons">
                            <button 
                              className="pricing_subscribe-btn"
                              onClick={() => handleSubscriptionAction(sub)}
                            >
                              Get Started
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>

                {/* Additional Services Row */}
                <div className="pricing_services-row">
                  {subscriptionTypes
                    .filter(sub => sub.type === 'service')
                    .map((sub, index) => (
                      <div key={index} className="pricing_subscription-card">
                        <h2>{sub.title}</h2>
                        <p className="pricing_sub-description">{sub.description}</p>
                        
                        <div className="pricing_duration-options">
                          {sub.durations.map((duration, idx) => (
                            <div key={idx} className="pricing_duration-item">
                              <div className="pricing_duration-header">
                                <span className="pricing_duration">{duration.months} Month</span>
                                <div className="pricing_price-container">
                                  <span className="pricing_price">{duration.price}</span>
                                  <button
                                    className="pricing_edit-btn"
                                    onClick={() => handlePriceEdit(
                                      sub.productType,
                                      '1_month',
                                      duration.price.replace(/[^0-9]/g, ''),
                                      !showEgyptianPrices
                                    )}
                                  >
                                    Edit
                                  </button>
                                </div>
                              </div>
                              {renderPriceInput(sub, '1_month')}
                              <ul className="pricing_features-list">
                                {duration.features.map((feature, fidx) => (
                                  <li key={fidx}>{feature}</li>
                                ))}
                              </ul>
                              <div className="pricing_subscription-buttons">
                                <button 
                                  className="pricing_subscribe-btn"
                                  onClick={() => handleSubscriptionAction(sub)}
                                >
                                  Get Started
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
      </div>
            </section>
          </>
        )}
      </main>
      
      <DashboardFooter />
    </div>
  );
};

export default Pricing;
